import { withTranslation } from 'react-i18next';
import { ISelectAvatarItemViewProps } from './interfaces';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useStyles } from './styles';
import { DeleteIcon } from '@icons/DeleteIcon';
import { EditIcon } from '@icons/EditIcon';
import { ReactComponent as GenericAvatarIcon } from '@public/images/generic_avatar.svg';
import { colors } from '@constants/colors';
import { isDefaultAvatar } from '@helpers/avatar';

/**
 * Renders one avatar card of the selection step.
 *
 * @param props.avatar - Avatar the card represents.
 * @param props.isSelectedAvatar - Whether the card is the current selection.
 * @param props.setSelectedAvatar - Selects this avatar on click.
 * @param props.editAvatarClick - Opens the avatar for editing.
 * @param props.deleteAvatarClick - Asks for deletion of the avatar.
 * @param props.canEdit - Whether editing is allowed for this avatar.
 * @param props.canDelete - Whether deletion is allowed for this avatar.
 */
const SelectAvatarItemView = ({
  avatar,
  isSelectedAvatar,
  setSelectedAvatar,
  editAvatarClick,
  deleteAvatarClick,
  canEdit,
  canDelete,
  t: translate,
}: ISelectAvatarItemViewProps) => {
  const classes = useStyles(isSelectedAvatar);
  const isDefault = isDefaultAvatar(avatar);

  return (
    <Box sx={classes.root}>
      <Card sx={classes.card}>
        <Box sx={classes.avatarPicContainer}>
          {avatar?.avatarPic && (
            <CardMedia
              component="img"
              image={avatar.avatarPic}
              alt="Avatar Miniature"
              sx={classes.avatarPic}
              onClick={() => {
                setSelectedAvatar(avatar);
              }}
            />
          )}
          {!avatar?.avatarPic && (
            <Box
              sx={classes.emptyAvtarPic}
              onClick={() => {
                setSelectedAvatar(avatar);
              }}
            >
              <GenericAvatarIcon style={{ fill: colors.black }} />
            </Box>
          )}
          {/* The default avatar carries no render of its own, so its generic
          icon is the same one an avatar of the user's shows while its Avaturn
          render has not arrived. The badge is what tells the two apart. */}
          {isDefault && (
            <Typography sx={classes.defaultBadge}>
              {translate('userAvatarPage.defaultAvatar')}
            </Typography>
          )}
        </Box>
        {/* The default avatar is not a record of the user's — it can be
        selected, but never edited or deleted, so the actions row is dropped
        rather than rendered disabled. */}
        {!isDefault && (canEdit || canDelete) && (
          <CardContent sx={classes.actions}>
            <Tooltip
              title={translate('tooltips.deleteAvatar')}
              componentsProps={{
                tooltip: {
                  sx: {
                    textAlign: 'center',
                  },
                },
              }}
            >
              <IconButton
                disableRipple
                sx={classes.action(canDelete)}
                onClick={() => deleteAvatarClick(avatar)}
              >
                <DeleteIcon sx={classes.actionIcon} />
              </IconButton>
            </Tooltip>
            <Tooltip title={translate('tooltips.edit')}>
              <IconButton
                disableRipple
                sx={classes.action(canEdit)}
                onClick={() => editAvatarClick(avatar)}
              >
                <EditIcon sx={classes.actionIcon} />
              </IconButton>
            </Tooltip>
          </CardContent>
        )}
      </Card>
    </Box>
  );
};

export default withTranslation()(SelectAvatarItemView);
